import { z } from 'zod';
import { API_FIELDS_CONTACT as CONTACT } from '@enroller/admissions-components';

export const internationalAddressDefaultValues = {
  [CONTACT.addressLine1]: '',
  [CONTACT.addressLine2]: '',
  [CONTACT.city]: '',
  [CONTACT.postalCode]: '',
  [CONTACT.country]: '',
};

export const USAAddressDefaultValues = {
  [CONTACT.addressLine1]: '',
  [CONTACT.addressLine2]: '',
  [CONTACT.city]: '',
  [CONTACT.postalCode]: '',
  [CONTACT.state]: '',
};

// TODO: change this once upload is implemented and we know data format
export const documentDefaultValue = [{ id: '' }];

export const internationalAddressSchema = z.object({
  [CONTACT.addressLine1]: z.string().min(1, 'Address Line 1 is required'),
  [CONTACT.addressLine2]: z.string().optional(),
  [CONTACT.postalCode]: z.string().min(1, 'Postal Code is required'),
  [CONTACT.city]: z.string().min(1, 'City is required'),
  [CONTACT.country]: z.string().min(1, 'Country is required'),
});

export const internationalAddressOptionalSchema = z.object({
  [CONTACT.addressLine1]: z.string().optional(),
  [CONTACT.addressLine2]: z.string().optional(),
  [CONTACT.postalCode]: z.string().optional(),
  [CONTACT.city]: z.string().optional(),
  [CONTACT.country]: z.string().optional(),
});

export const USAAddressSchema = z.object({
  [CONTACT.addressLine1]: z.string().min(1, 'Address Line 1 is required'),
  [CONTACT.addressLine2]: z.string().optional(),
  [CONTACT.postalCode]: z.string().min(1, 'Postal Code is required'),
  [CONTACT.city]: z.string().min(1, 'City is required'),
  [CONTACT.state]: z.string().min(1, 'State is required'),
});

export const USAAddressOptionalSchema = z
  .object({
    [CONTACT.addressLine1]: z.string().optional(),
    [CONTACT.addressLine2]: z.string().optional(),
    [CONTACT.postalCode]: z.string().optional(),
    [CONTACT.city]: z.string().optional(),
    [CONTACT.state]: z.string().optional(),
  })
  .optional();

export const selectionRangeTableOptionalSchema = z.record(z.number().or(z.string().max(0)));

export const selectionRangeTableSchema = selectionRangeTableOptionalSchema.refine(
  (data) => {
    return Object.values(data).some((value) => value !== '');
  },
  {
    message: 'Please fill all the fields in the table',
  },
);

export const getDocumentSchema = (name: string) =>
  z
    .array(
      z.object({
        id: z.string(),
      }),
    )
    .refine((data) => data[0]?.id, { message: `${name} is required`, path: [] });

export const documentOptionalSchema = z
  .array(
    z.object({
      id: z.string(),
    }),
  )
  .optional();

export const getPhotoSchema = (name: string) =>
  z
    .object({
      id: z.string(),
    })
    .refine((data) => !!data.id, { message: `${name} is required`, path: [] });

export const photoOptionalSchema = z.object({
  id: z.string().optional(),
});

export const getDependentFieldSchema = (dependentFieldName: string, fieldValue: string) => {
  return z
    .object({
      value: z.string().min(1, 'Please specify'),
      [dependentFieldName]: z.string().optional(),
    })
    .refine(
      (data) => {
        // Make dependent field required if the value matches fieldValue
        if (data.value === fieldValue) {
          return !!data[dependentFieldName] && data[dependentFieldName].trim() !== '';
        }
        return true;
      },
      {
        message: 'Please specify',
        path: [dependentFieldName],
      },
    );
};

export const getDependentFieldOptionalSchema = (dependentFieldName: string) => {
  return z.object({
    value: z.string().optional(),
    [dependentFieldName]: z.string().optional(),
  });
};

export const emailSchema = z.string({ required_error: 'Email address is required' }).email({ message: 'Email address is invalid' });

export const getUrlSchema = (errorMessage: string) => z.string({ required_error: errorMessage }).url({ message: 'URL is invalid' });
